import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { captureSmartTableEvent } from "./analytics";
import { UpdateSmartTableRequest, SmartTableDetail, unwrapSmartTableEntityPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const useUpdateSmartTable = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<SmartTableDetail, unknown, { id: string } & UpdateSmartTableRequest>({
    mutationFn: async ({ id, ...payload }) => {
      const response = await fetch(ENDPOINTS.smart_table(wid, id), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(payload),
      });
      const responsePayload = await handleResponse(response);
      return unwrapSmartTableEntityPayload<SmartTableDetail>(responsePayload, "smart_table");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, "list"] });
      queryClient.invalidateQueries({ queryKey: ["smart-tables", wid, variables.id] });
      captureSmartTableEvent("Smart Table Updated", {
        workspace_id: wid,
        smart_table_id: variables.id,
        has_title_update: Boolean(variables.title),
      });
    },
  });
};
